import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { getCancellationErrorMessage } from '@/returns/pages/TransactionDetailPage/helpers'
import returnsServices from '@/returns/services/services'

import { getGiftCardBlockingMessage } from './helpers'
import { transformResponseGetGiftCard } from './transformers'

const useCancelGiftCard = ({ orderId, fetchData, fetchTimeline, setToastInfo, handleChargebackError }) => {
  const { t } = useTranslation()
  const [showCancelGiftCardDrawer, setShowCancelGiftCardDrawer] = useState(false)
  const [notes, setNotes] = useState('')
  const [issueOriginalPaymentMethod, setIssueOriginalPaymentMethod] = useState(false)
  const [giftCardErrorMessage, setGiftCardErrorMessage] = useState(null)

  const { fetchData: cancelGiftCard, loading: cancellingGiftCard } = useFetch(returnsServices.cancelGiftCard, {
    doInitialRequest: false,
  })

  const {
    data: giftCard,
    loading: fetchingGiftCard,
    fetchDataCancellable: fetchGiftCard,
    setData: setGiftCard,
  } = useFetch(returnsServices.getGiftCard, {
    doInitialRequest: false,
    transformResponse: transformResponseGetGiftCard,
  })

  const refreshGiftCard = async () => {
    setGiftCardErrorMessage(null)

    try {
      await fetchGiftCard({ orderId })
    } catch (err) {
      setGiftCard(null)
      setGiftCardErrorMessage(
        getCancellationErrorMessage(err, t('No se pudo consultar el saldo de la tarjeta regalo.'))
      )
    }
  }

  const openCancelGiftCardDrawer = async () => {
    setShowCancelGiftCardDrawer(true)
    await refreshGiftCard()
  }

  const closeCancelGiftCardDrawer = () => {
    setShowCancelGiftCardDrawer(false)
    setNotes('')
    setIssueOriginalPaymentMethod(false)
    setGiftCard(null)
    setGiftCardErrorMessage(null)
  }

  const handleCancelGiftCard = async () => {
    try {
      await cancelGiftCard({ orderId, notes, issueOriginalPaymentMethod })
      setShowCancelGiftCardDrawer(false)
      setToastInfo({ show: true, message: t('Tarjeta regalo cancelada correctamente'), type: 'success' })
      fetchData()
      fetchTimeline()
    } catch (err) {
      if (handleChargebackError(err)) return

      setToastInfo({
        show: true,
        message: getCancellationErrorMessage(err, t('No se pudo anular la tarjeta regalo.')),
        type: 'error',
      })
      await refreshGiftCard()
    }
  }

  const giftCardBlockingMessage = getGiftCardBlockingMessage({ giftCard, errorMessage: giftCardErrorMessage, t })
  const canVoidGiftCard = !fetchingGiftCard && !!giftCard && !giftCardBlockingMessage

  return {
    showCancelGiftCardDrawer,
    openCancelGiftCardDrawer,
    closeCancelGiftCardDrawer,
    handleCancelGiftCard,
    cancellingGiftCard,
    notes,
    setNotes,
    issueOriginalPaymentMethod,
    setIssueOriginalPaymentMethod,
    giftCard,
    fetchingGiftCard,
    giftCardBlockingMessage,
    canVoidGiftCard,
  }
}

export default useCancelGiftCard
